import {
  Box,
  Button,
  IconButton,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import styles from './style.module.scss';
import {t} from 'i18next';
import React, {useState} from 'react';
import {ReactComponent as VeryDissatisfiedIcon} from './../../../src/assests/svg/sentiment_very_dissatisfied.svg';
import {ReactComponent as DissatisfiedIcon} from './../../../src/assests/svg/sentiment_dissatisfied.svg';
import {ReactComponent as AdequateIcon} from './../../../src/assests/svg/sentiment_adequate.svg';
import {ReactComponent as SatisfiedIcon} from './../../../src/assests/svg/sentiment_satisfied.svg';
import {ReactComponent as VerySatisfiedIcon} from './../../../src/assests/svg/sentiment_very_satisfied.svg';
import AurButton from '../common/aur-button/AurButton';
import {
  setFeedbackPromptVisibility,
  setFeedbackVisibility,
} from '../../redux/slices/askLioSlice';
import {useDispatch, useSelector} from 'react-redux';
import {AurTextField} from '../common/aur-text-field/AurTextField';
import {
  AskLioFeedbackModel,
  AskLioMessage,
} from '../../types/ask-lio/askLioTypes';
import {RootState} from '../../redux/store';
import {submitAskLioFeedback} from '../../api/AskLioService';


const EmojiTooltip = styled(({className, ...props}: TooltipProps) => (
  <Tooltip {...props} arrow classes={{popper: className}} />
))(({theme}) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    fontWeight: 'bold',
  },
}));

type AskLioFeedbackProps = {
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

type RatingEmojiProps = {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title?: string;
  active?: boolean;
  onClick?: () => void;
};

const AurAskLioFeedback: React.FC<AskLioFeedbackProps> = ({
  className,
  onClick,
}) => {
  const dispatch = useDispatch();
  const entity = useSelector((state: RootState) => state.askLioSlice.entity);
  const task = useSelector((state: RootState) => state.askLioSlice.task); 
  
  const askLioQuery = useSelector(
    (state: RootState) => state.askLioSlice.askLioQuery
  );

  const initialFeedback: AskLioFeedbackModel = {
    rating: null,
    messages: askLioQuery.flatMap((item): AskLioMessage[] => [
      {
        date: item.queryDate || '',
        message: item.query,
        originator: 'user',
      },
      {
        date: item.responseDate || '',
        message: item.content,
        originator: 'asklio',
      },
    ]),
    comment: '',
    entity: entity,
    task: task,
  };

  const [feedback, setFeedback] =
    useState<AskLioFeedbackModel>(initialFeedback);

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      const params = {
        ...feedback,
        messages: JSON.stringify(feedback.messages),
      };
      await submitAskLioFeedback(params);

      if (onClick) {
        onClick(e);
      }
    } catch (error) {
      //error
    }
  };

  return (
    <>
      <Box className={`${styles.aurBoxFeedback} ${className}`}>
        <Box className={`${styles.aurBoxRating}`}>
          <Box className={`${styles.aurBoxRatingSlogan}`}>
            <Typography
              aria-label={t('askLio.feedback.rateSloganText')}
              className={`${styles.aurBoxFeedbackTypography}`}
            >
              {t('askLio.feedback.rateSloganText')}
            </Typography>
            <Button
              aria-label={t('askLio.feedback.rateSkipButtonText')}
              tabIndex={0}
              className={`${styles.aurBoxRatingTypographyButton}`}
              onClick={() => {
                dispatch(setFeedbackVisibility(false));
                dispatch(setFeedbackPromptVisibility(true));
              }}
            >
              {t('askLio.feedback.rateSkipButtonText')}
            </Button>
          </Box>
          <Box className={`${styles.aurBoxRatingEmoji}`}>
            <RatingEmoji
              Icon={VeryDissatisfiedIcon}
              title={`${t('askLio.feedback.rateVeryDissatisfiedText')}`}
              onClick={() => setFeedback({...feedback, rating: 1})}
              active={feedback?.rating == 1}
            />
            <RatingEmoji
              Icon={DissatisfiedIcon}
              title={`${t('askLio.feedback.rateDissatisfiedText')}`}
              onClick={() => setFeedback({...feedback, rating: 2})}
              active={feedback?.rating == 2}
            />
            <RatingEmoji
              Icon={AdequateIcon}
              title={`${t('askLio.feedback.rateAdequateText')}`}
              onClick={() => setFeedback({...feedback, rating: 3})}
              active={feedback?.rating == 3}
            />
            <RatingEmoji
              Icon={SatisfiedIcon}
              title={`${t('askLio.feedback.rateSatisfiedText')}`}
              onClick={() => setFeedback({...feedback, rating: 4})}
              active={feedback?.rating == 4}
            />
            <RatingEmoji
              Icon={VerySatisfiedIcon}
              title={`${t('askLio.feedback.rateVerySatisfiedText')}`}
              onClick={() => setFeedback({...feedback, rating: 5})}
              active={feedback?.rating == 5}
            />
          </Box>
          {feedback?.rating && (
            <Box>
              <AurTextField
                className={`${styles.aurAskLioQueryTextBox} ${styles.aurAskLioOptionalFeedbackField}`}
                placeholder={t('askLio.feedback.rateOptionalFeedback')}
                ariaLabel={t('askLio.feedback.rateOptionalFeedback')}
                variant="outlined"
                fullWidth
                multiline
                minRows={1}
                maxRows={4}
                onChange={(e) =>
                  setFeedback({
                    ...feedback,
                    comment: e.target.value,
                  })
                }
              />
            </Box>
          )}
          <Box className={`${styles.aurBoxRatingSubmit}`}>
            <AurButton
              type={'submit'}
              label={t('askLio.feedback.rateSubmitButtonText')}
              dataTestId="aurAskLioRatingSubmitButton"
              classNames={`${styles.aurBoxRatingSubmitButton} ${!feedback?.rating && styles.aurBoxRatingSubmitButtonDisabled}`}
              disabled={!feedback?.rating}
              onClick={(e) => {
                handleSubmit(e);
              }}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

const RatingEmoji: React.FC<RatingEmojiProps> = ({
  Icon,
  title,
  active = false,
  onClick,
}) => {
  return (
    <>
      <EmojiTooltip title={title} placement="top" arrow>
        <IconButton
          aria-label={`${title}`}
          className={`${styles.aurBoxRatingEmojiButton} ${active ? styles.aurBoxRatingEmojiButtonActive : ''}`}
          onClick={onClick}
        >
          <Icon aria-hidden="true" />
        </IconButton>
      </EmojiTooltip>
    </>
  );
};

export default AurAskLioFeedback;
